import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Form, Alert, Spinner } from "react-bootstrap";
import { useSearchStore } from "../../../store/useSearchStore";
import { Link } from "react-router-dom";

interface SerpsTabProps {
  query: string;
}

interface SerpResult {
  rank: number;
  title: string;
  link: string;
  snippet: string;
}

const locations = ["India", "United States", "Brazil", "Mexico"];

const SerpsTab: React.FC<SerpsTabProps> = ({ query }) => {
  const [loading, setLoading] = useState(false);
  const [location, setLocation] = useState("India");
  const [error, setError] = useState("");
  const [searchResults, setSearchResults] = useState<SerpResult[]>([]);

  // Get Zustand store methods
  const { getFromCache, addToCache } = useSearchStore();

  const fetchSerpData = useCallback(async () => {
    if (!query) return;

    try {
      setLoading(true);
      setError("");

      // Check cache first
      const cachedResults = getFromCache(query, location);

      if (cachedResults) {
        setSearchResults(cachedResults);
        return;
      }

      const response = await axios.get(
        "https://api.performancedashboard.io/api/tools/serp-data",
        // "http://localhost:3000/api/tools/serp-data",
        {
          params: {
            q: query,
            location: location,
            hl: "hi",
            gl: "in",
            google_domain: "google.co.in",
            async: "true",
          },
        }
      );

      if (!response.data?.data) {
        throw new Error("Invalid API response structure");
      }

      const results = response.data.data;

      if (Array.isArray(results) && results.length > 0) {
        // Add to cache
        addToCache(query, results, location);
        setSearchResults(results);
      } else {
        setSearchResults([]);
      }
    } catch (err) {
      console.error("Error fetching SERP data:", err);
      const errorMessage = axios.isAxiosError(err)
        ? err.response?.data?.error || err.message
        : "Failed to fetch SERP data";
      setError(`Error: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  }, [query, location, getFromCache, addToCache]);

  useEffect(() => {
    fetchSerpData();
  }, [fetchSerpData]);

  const handleLocationChange = (newLocation: string) => {
    setLocation(newLocation);
  };

  return (
    <>
      {/* Location Selector */}
      <Form.Select
        value={location}
        onChange={(e) => handleLocationChange(e.target.value)}
        style={{ width: "100%", marginBottom: 2, marginTop: 2 }}
      >
        {locations.map((loc) => (
          <option key={loc} value={loc}>
            {loc}
          </option>
        ))}
      </Form.Select>

      {/* Error Handling */}
      {error && <Alert variant="danger">{error}</Alert>}

      {/* Loading Indicator */}
      {loading && (
        <div style={{ display: "flex", justifyContent: "center", padding: 24 }}>
          <Spinner />
        </div>
      )}

      {/* No Results Found */}
      {!loading && searchResults.length === 0 && !error && (
        <p style={{ textAlign: "center", padding: 24 }}>No results found</p>
      )}

      {/* Search Results */}
      {!loading &&
        searchResults.map((result, index) => (
          <div
            key={index}
            style={{
              marginBottom: 2,
              borderBottom: "1px solid #ddd",
              paddingBottom: 1,
            }}
          >
            <p style={{ color: "#006621" }}>{new URL(result.link).hostname}</p>

            <p style={{ color: "#1a0dab", cursor: "pointer" }}>
              <Link
                to={result.link}
                target="_blank"
                style={{ textDecoration: "none" }}
              >
                {result.title}
              </Link>
            </p>

            <p style={{ color: "textSecondary" }}> {result.snippet}</p>
          </div>
        ))}
    </>
  );
};

export default SerpsTab;
